import * as fileUtils from "fileUtils";
import { TEMPLATE_OVERRIDE_ALLOW_PDF } from "flags";
import { useState, useCallback, useMemo } from "react";
import { useFeatureFlag } from "store/hooks/featureFlagHooks";

const DOCX_MIMETYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const PDF_MIMETYPE = "application/pdf";

export const useTemplateOverride = ({
  template,
  submissionId,
  layerId,
  setTemplateOverride,
  removeTemplateOverride,
  refetchTemplateOverrides,
}) => {
  const [loadingAction, setLoadingAction] = useState(null);
  const allowPdfOverride = useFeatureFlag(TEMPLATE_OVERRIDE_ALLOW_PDF);

  const allowedMimeTypes = useMemo(
    () => (allowPdfOverride ? [DOCX_MIMETYPE, PDF_MIMETYPE] : [DOCX_MIMETYPE]),
    [allowPdfOverride]
  );

  const upload = useCallback(
    async (file) => {
      if (
        !file ||
        !allowedMimeTypes.includes(file.type) ||
        !template?.id ||
        !submissionId
      ) {
        return null;
      }

      setLoadingAction("upload");

      const overrideFileId = await fileUtils.createFileEntry(
        file,
        false,
        allowedMimeTypes
      );

      await setTemplateOverride({
        submissionId,
        layerId,
        templateId: template.id,
        overrideFileId,
      }).unwrap();

      await refetchTemplateOverrides();

      setLoadingAction(null);

      return overrideFileId;
    },
    [
      template,
      submissionId,
      layerId,
      allowedMimeTypes,
      setTemplateOverride,
      refetchTemplateOverrides,
    ]
  );

  const remove = useCallback(async () => {
    if (!template?.id || !submissionId) return;

    try {
      setLoadingAction("remove");
      await removeTemplateOverride({
        submissionId,
        layerId,
        templateId: template.id,
      }).unwrap();

      await refetchTemplateOverrides();
    } finally {
      setLoadingAction(null);
    }
  }, [
    template,
    submissionId,
    layerId,
    removeTemplateOverride,
    refetchTemplateOverrides,
  ]);

  const download = useCallback(
    async (fileId, filename) => {
      if (!fileId) return;
      try {
        setLoadingAction("download");
        await fileUtils.saveFile(fileId, filename ?? `${template?.title}.docx`);
      } finally {
        setLoadingAction(null);
      }
    },
    [template]
  );

  return { upload, remove, download, loadingAction };
};
